'use client';

import React, { Fragment, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const MAX_ITEMS_PER_SECTION = 10;

const faqData = [
  {
    title: null,
    questions: [
      {
        question: 'Trovanorma sostituisce il lavoro di un professionista?',
        answer:
          'No. Trovanorma è pensata per supportare il professionista, non per sostituirlo. Trovanorma aiuta a ridurre i tempi di ricerca, organizzare le informazioni e formulare pareri completi e documentati, ma la decisione finale e la responsabilità restano sempre nelle mani del professionista. È come avere un assistente senior che prepara la base, così tu puoi concentrarti sulle valutazioni strategiche.',
      },
    ],
  },
  {
    title: 'Funzionamento',
    questions: [
      {
        question: 'In cosa Trovanorma è diverso da ChatGPT o altre AI generaliste?',
        answer:
          'Trovanorma è un’AI specializzata, progettata per operare esclusivamente su un database legale proprietario e verificato. Non naviga su Internet, non inventa informazioni e fornisce risposte basate solo su fonti ufficiali. Dove altri puntano alla creatività, noi puntiamo alla certezza documentale, così ogni tuo parere è supportato da prove concrete.',
      },
      {
        question: 'Quanto tempo impiega Trovanorma a fornire una risposta?',
        answer:
          'Per un parere completo e articolato, con riferimenti a normativa e sentenze, servono in media un paio di minuti. Per ricerche più semplici, la risposta arriva in pochi secondi. Così risparmi ore di ricerca e riduci il rischio di errori.',
      },
      {
        question: 'Posso consultare le fonti direttamente dalla piattaforma?',
        answer:
          'Sì. Ogni risposta è accompagnata da link diretti alle fonti ufficiali (Gazzetta Ufficiale, Cassazione, ADE, INPS, INAIL, ecc.), in modo da poter verificare autonomamente ogni informazione e dimostrare l’affidabilità del tuo lavoro.',
      },
      {
        question: 'Posso salvare e organizzare le mie ricerche per riutilizzarle?',
        answer:
          'Sì. Puoi salvare ricerche, pareri e documenti generati e consultarli o riutilizzarli in qualsiasi momento, così da costruire la tua libreria di contenuti sempre pronta all’uso.',
      },
    ],
  },
  {
    title: 'Copertura normativa',
    questions: [
      {
        question: 'Sono incluse le norme europee e le sentenze della Corte di Giustizia UE?',
        answer:
          'Sì. Oltre alla normativa nazionale, includiamo la normativa primaria dell’Unione Europea e le pronunce della Corte di Giustizia UE, così puoi avere un quadro completo e aggiornato anche a livello internazionale.',
      },
      {
        question: 'Vengono coperte anche le circolari e gli interpelli dell’Agenzia delle Entrate?',
        answer:
          'Sì. Trovanorma integra circolari, interpelli, risoluzioni e provvedimenti dell’Agenzia delle Entrate, insieme alle comunicazioni di INPS e INAIL, garantendoti una copertura completa di tutte le fonti operative.',
      },
      {
        question: 'Ogni quanto vengono aggiornati i dati?',
        answer:
          'La sincronizzazione dalle fonti ufficiali avviene quotidianamente. Questo ti assicura di lavorare sempre sulla versione più recente di norme e prassi, evitando rischi di applicare contenuti superati.',
      },
    ],
  },
  {
    title: 'Sicurezza e privacy',
    questions: [
      {
        question: 'I miei dati e quelli dei miei clienti sono al sicuro?',
        answer:
          'Sì. Utilizziamo crittografia end-to-end e siamo pienamente conformi al GDPR. Le tue ricerche restano private e non vengono mai utilizzate per addestrare modelli di AI, nostri o di terzi. Il tuo lavoro resta solo tuo.',
      },
      {
        question: 'Dove sono conservati i dati?',
        answer:
          'In Europa, su server AWS conformi ai più alti standard internazionali di sicurezza e certificati (ISO 27001, SOC 3), così puoi lavorare in piena tranquillità anche su dati sensibili.',
      },
    ],
  },
  {
    title: 'Abbonamenti e prova gratuita',
    questions: [
      {
        question: 'Cosa succede al termine del periodo di beta gratuita?',
        answer:
          'Potrai scegliere se attivare un abbonamento. Gli utenti che partecipano alla beta riceveranno un’offerta speciale e permanente, comunicata in anticipo e senza alcun obbligo di acquisto, per continuare a utilizzare tutte le funzionalità senza interruzioni.',
      },
      {
        question: 'Posso disdire in qualsiasi momento?',
        answer:
          'Sì. Puoi interrompere l’abbonamento in qualsiasi momento: la disattivazione avverrà alla fine del ciclo di fatturazione in corso, senza costi aggiuntivi o penali.',
      },
    ],
  },
];

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Assicura che i ref siano assegnati prima dell'uso
  useEffect(() => {
    contentRefs.current = contentRefs.current.slice(0, faqData.length * MAX_ITEMS_PER_SECTION);
  }, []);

  return (
    <section id="faq" className="py-16 px-4 md:px-8 bg-white scroll-mt-12">
      <div className="max-w-xl md:max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-primary font-funnel mb-6">
          I tuoi dubbi, le nostre <span className="text-accent">risposte</span>
        </h2>
        <p className="text-lg text-gray-600 mb-12">
          Risposte chiare e trasparenti ai dubbi più comuni, per decidere con tutto ciò di cui hai
          bisogno.
        </p>
        <div className="">
          {faqData.map((section, sectionIdx) => {
            return (
              <Fragment key={sectionIdx}>
                {section.title && (
                  <div className="text-primary-500 uppercase text-sm text-left font-bold border-b border-neutral-200 pt-8 pb-1">
                    {section.title}
                  </div>
                )}
                {section.questions.map((item, itemIdx) => {
                  const index = sectionIdx * MAX_ITEMS_PER_SECTION + itemIdx;
                  const isOpen = openIndex === index;
                  return (
                    <div key={item.question} className="pt-4">
                      <h3>
                        <button
                          onClick={() => handleToggle(index)}
                          className="flex w-full items-center justify-between text-left text-gray-900 cursor-pointer focus:outline-none"
                          aria-expanded={isOpen}
                          aria-controls={`faq-content-${index}`}
                        >
                          <span className="text-base font-medium">{item.question}</span>
                          <span className="ml-6 flex h-7 items-center">
                            <ChevronDown
                              className={`text-accent transform transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                              aria-hidden="true"
                              size={24}
                            />
                          </span>
                        </button>
                      </h3>
                      <div
                        ref={el => {
                          contentRefs.current[index] = el;
                        }}
                        id={`faq-content-${index}`}
                        role="region"
                        aria-labelledby={`faq-question-${index}`}
                        className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
                        style={{
                          maxHeight: isOpen
                            ? `${contentRefs.current[index]?.scrollHeight}px`
                            : '0px',
                        }}
                      >
                        <div className="pt-4 pr-6">
                          <p className="text-base text-left text-gray-600">{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
